import { toast } from '@core/component/Toast/Toast';
import { recipientEntityMapper, useContacts } from '@core/user';
import { useVisibleCalendarsQuery } from '@queries/calendar/calendars';
import {
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
} from '@queries/calendar/mutations';
import type { CalendarUpdateScope } from '@service-email/client';
import { type Accessor, createMemo } from 'solid-js';
import {
  calendarEventToEditorInitialValues,
  type EventEditorDisabledFields,
  type EventEditorSubmitValues,
} from '../components/composer/event-form-model';
import {
  type CalendarEvent,
  DEFAULT_CALENDAR_SOURCE,
  reminderCalendarIdOf,
} from '../types';
import {
  calendarDisplayLabel,
  spansMultipleInboxes,
} from '../utils/calendar-label';
import {
  guestListChanged,
  viewerCanEditGuests,
} from '../utils/event-guest-editing';

const EDIT_DISABLED_FIELDS = {
  calendar: true,
} satisfies EventEditorDisabledFields;

/**
 * Whether the editor addresses the copy whose reminders Macro's alerts
 * follow and whose guests and conferencing Macro records. Another copy's
 * reminders are Google's own and never fire here, and guests or a Meet link
 * written onto another copy never reach the event Macro shows.
 */
function editsPrimaryCopy(event: CalendarEvent) {
  return reminderCalendarIdOf(event) === event.calendarId;
}

interface UseEventEditorProps {
  event: Accessor<CalendarEvent | undefined>;
  onSaved: () => void;
}

/** Shared create/edit query and mutation orchestration for any editor shell. */
export function useEventEditor(props: UseEventEditorProps) {
  const isEdit = () => props.event() !== undefined;
  const initialValues = createMemo(() => {
    const event = props.event();
    return event ? calendarEventToEditorInitialValues(event) : undefined;
  });
  const initialLines = createMemo(() => props.event()?.recurrenceLines ?? []);

  // Event edits also need calendar metadata to resolve reminders that still
  // follow the calendar defaults.
  const calendarsQuery = useVisibleCalendarsQuery();
  const contacts = useContacts();

  const guestOptions = createMemo(() =>
    contacts().map(recipientEntityMapper('user'))
  );
  const writableCalendars = createMemo(
    () => calendarsQuery.data?.filter((calendar) => calendar.isWritable) ?? []
  );
  const spansInboxes = createMemo(() =>
    spansMultipleInboxes(writableCalendars())
  );
  const calendarOptions = createMemo(() => {
    const event = props.event();
    if (event) {
      const calendarId = event.calendarId ?? event.calendar.id;
      const calendars = calendarsQuery.data ?? [];
      const calendar = calendars.find(
        (candidate) => candidate.id === calendarId
      );
      const reminderCalendar = calendars.find(
        (candidate) => candidate.id === reminderCalendarIdOf(event)
      );
      return [
        {
          id: calendarId,
          label: event.calendar.name || 'Calendar',
          color: event.calendar.color,
          defaultReminders: reminderCalendar?.defaultReminders,
          isPrimary: calendar?.isPrimary ?? event.calendar.isPrimary,
        },
      ];
    }

    return writableCalendars().map((calendar) => ({
      id: calendar.id,
      label: calendarDisplayLabel(calendar, spansInboxes()),
      color: calendar.color ?? DEFAULT_CALENDAR_SOURCE.color,
      defaultReminders: calendar.defaultReminders,
      isPrimary: calendar.isPrimary,
    }));
  });

  const create = useCreateCalendarEventMutation({
    onSuccess: props.onSaved,
    onError: (error) => {
      toast.failure('Failed to create event', { subtext: error.message });
    },
  });
  const update = useUpdateCalendarEventMutation({
    onSuccess: props.onSaved,
    onError: (error) => {
      toast.failure('Failed to update event', { subtext: error.message });
    },
  });

  const pending = () => create.isPending || update.isPending;

  const save = (
    values: EventEditorSubmitValues,
    scope?: CalendarUpdateScope
  ) => {
    if (pending()) return;

    const event = props.event();
    if (event) {
      const effectiveScope: CalendarUpdateScope = scope ?? 'all';
      const targetsOneOccurrence = effectiveScope === 'this_event';

      // A single occurrence has no recurrence of its own, and the provider
      // rejects a recurrence-carrying patch scoped to one event, so recurrence
      // lines only travel with a whole-series edit. Under `this_event` the
      // recurrence controls are read-only, so any diff here is an incidental
      // re-serialization (e.g. a date-dependent preset regenerated by a
      // reschedule), not an intended rule change.
      const recurrenceChanged =
        !targetsOneOccurrence &&
        values.recurrenceLines !== undefined &&
        values.recurrenceLines.join('\n') !== initialLines().join('\n');

      update.mutate({
        eventId: event.eventId,
        calendarId: event.calendarId,
        scope: effectiveScope,
        recurrenceId: targetsOneOccurrence
          ? (event.recurrenceId ?? event.occurrenceKey)
          : undefined,
        occurrenceKey: targetsOneOccurrence ? event.occurrenceKey : undefined,
        patch: {
          title: values.title,
          time: values.time,
          location: values.location,
          description: values.description,
          ...(recurrenceChanged
            ? { recurrenceLines: values.recurrenceLines }
            : {}),
          ...(guestListChanged(event, values.guestEmails)
            ? {
                attendees: values.guestEmails.map((email) => ({ email })),
              }
            : {}),
          ...(values.conference ? { conference: values.conference } : {}),
          ...(values.reminders ? { reminders: values.reminders } : {}),
          ...(values.outOfOffice ? { outOfOffice: values.outOfOffice } : {}),
        },
      });
      return;
    }

    create.mutate({
      title: values.title,
      time: values.time,
      calendarId: values.calendarId,
      recurrenceLines: values.recurrenceLines ?? [],
      location: values.location === '' ? undefined : values.location,
      description: values.description === '' ? undefined : values.description,
      attendees: values.guestEmails.map((email) => ({ email })),
      ...(values.conference ? { conference: values.conference } : {}),
      ...(values.reminders ? { reminders: values.reminders } : {}),
      ...(values.outOfOffice ? { outOfOffice: values.outOfOffice } : {}),
    });
  };

  const showRecurringEditNotice = () =>
    isEdit() &&
    ((props.event()?.recurrenceLines.length ?? 0) > 0 ||
      props.event()?.recurrenceId !== undefined);
  const disabledFields = createMemo<EventEditorDisabledFields | undefined>(
    () => {
      const event = props.event();
      if (!event) return undefined;
      const editsOtherCopy = !editsPrimaryCopy(event);
      return {
        ...EDIT_DISABLED_FIELDS,
        guests: editsOtherCopy || !viewerCanEditGuests(event),
        conference: editsOtherCopy,
        reminders: editsOtherCopy,
      };
    }
  );

  return {
    initialValues,
    disabledFields,
    calendarOptions,
    guestOptions,
    showRecurringEditNotice,
    pending,
    save,
  };
}
